import React from "react"
import styled, { css } from "styled-components"

import Footer from "components/footer"
import { Box, BoxProps, Flex } from "components/box"
import { useScrollToTop } from "hooks/useScrollToTop"

const containerCSS = css`
  display: flex;
  flex-direction: column;
  min-height: -webkit-fill-available;
`

export const FlexFormWrapper = styled.form`
  ${containerCSS}
  background-color: ${props => props.theme.colors.iceBlue};
`
export const FlexWrapper = styled(Flex)`
  ${containerCSS}
  background-color: ${props => props.theme.colors.paleGrey};
  min-height: 85vh;
`

const FooterStyle = styled(Box)`
  background-color: ${props => props.theme.colors.white};
  position: fixed;
  bottom: 0;
  width: 100%;
`

type WrapperProps = React.HTMLProps<HTMLFormElement> & BoxProps
type MainContainerProps = {
  pageWrapperComponent?: React.ComponentType
  mainContent: React.ReactNode
  footerFixed?: React.ReactNode
  hideFooter?: boolean
} & WrapperProps

/**
 * MainContainer is a layout component that accepts two 'slots' (See https://daveceddia.com/pluggable-slots-in-react-components/ to understand what a slot is)
 * Includes functionality to scroll to the top of the page on page navigation
 * Disables body scroll - see issue for more info (https://github.com/ThirtyMadison/cove/pull/358)
 *
 * @param mainContent The first slot that contains all of the page scrollable content (forms(), articles etc).
 * @param footerFixed The second slot that is the content 'fixed' on the bottom of the page (we use it for the CTAs / form submit buttons on mobile).
 * @param pageWrapperComponent Component that wraps mainContent. Currently we use FlexFormWrapper (for pages with the form) or FlexWrapper (for regular pages)
 *
 * All of the props for pageWrapperComponent are passed through the MainContainer
 */
export const MainContainer: React.FC<MainContainerProps> = ({
  mainContent,
  footerFixed,
  hideFooter,
  pageWrapperComponent,
  ...wrapperProps
}) => {
  useScrollToTop()
  const Container = (pageWrapperComponent as React.FC<WrapperProps>) || FlexWrapper

  return (
    <Container {...wrapperProps}>
      {/* Made it so that the footer is always after the element in the MainContainer */}
      <Box>
        {mainContent}
        {!hideFooter && <Footer />}
      </Box>
      <FooterStyle>{footerFixed}</FooterStyle>
    </Container>
  )
}
